// SPDX-FileCopyrightText: Copyright 2024 Dash0 Inc.

import { useShallow } from "zustand/react/shallow";

// Define a type for the selector function
export type Selector<S, U> = Parameters<typeof useShallow<S, U>>[0];

/**
 * Define a type for the useSelector function
 */
export type UseSelector<S> = <U = S>(selector?: Selector<S, U>) => U;

/**
 * Selector hook which allows to select a portion of an object while using shallow equality
 * Using `useShallow` from zustand under the hood
 *
 * @example
 * ```tsx
 * export const useOrganizationInfo: UseSelector<OrganizationInfo> = (selector) => {
 * 	const organizationInfo = useContext(context); // the object we want to select
 *
 * 	if (organizationInfo == null) {
 * 		throw new Error("useOrganizationInfo must be used within a OrganizationTechnicalIdProvider");
 * 	}
 *
 * 	return useSelector(organizationInfo, selector); // Returning the object
 * }
 * ```
 */
export function useSelector<S, U>(obj: S, selector?: Selector<S, U>): U {
	const selectorFn: Selector<S, U> = (state) => {
		if (selector) {
			return selector(state);
		}

		return state as unknown as U;
	};

	return useShallow<S, U>(selectorFn)(obj);
}
